<script setup lang="ts">
import { computed, ref } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import type { ReturnApplicationModalProps } from '@/stores/modal';
import { MODAL_TYPES, useModalStore } from '@/stores/modal';

const props = defineProps<ReturnApplicationModalProps>();
const modalStore = useModalStore();

const returnDate = ref(props.expectedReturnDate || '');
const returnReason = ref('');
const isSubmitting = ref(false);

const isSubmitDisabled = computed(() => !returnDate.value || isSubmitting.value);

const handleClose = () => {
  modalStore.close(MODAL_TYPES.RETURN_APPLICATION);
};

const handleConfirm = async () => {
  if (isSubmitDisabled.value) return;

  try {
    isSubmitting.value = true;
    await props.onConfirm({
      absenceId: props.absenceId,
      returnDate: returnDate.value,
      returnReason: returnReason.value.trim() || undefined,
    });
    handleClose();
  }
  finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <BaseModal title="복귀서 제출하기" width="420px" aria-label="복귀서 제출" @close="handleClose">
    <div class="flex flex-col gap-4 px-1 py-2">
      <p class="text-[14px] leading-[1.7] text-[#6b5a4d]">
        복귀 예정일을 확인하고, 필요하면 복귀 사유를 함께 입력해주세요.
      </p>

      <div class="flex flex-col gap-2">
        <label class="text-[15px] font-[800] text-[#2f241c]">복귀일 *</label>
        <input
          v-model="returnDate"
          type="date"
          class="h-[50px] w-full rounded-[10px] border border-[#e2d8d0] bg-white px-4 text-[15px] text-[#2f241c] outline-none transition focus:border-[#ee8544]"
        >
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-[15px] font-[800] text-[#2f241c]">복귀사유</label>
        <textarea
          v-model="returnReason"
          rows="4"
          class="w-full resize-none rounded-[10px] border border-[#e2d8d0] bg-white px-4 py-3 text-[15px] leading-[1.7] text-[#2f241c] outline-none transition focus:border-[#ee8544]"
          placeholder="복귀 사유가 있으면 입력해주세요."
        />
      </div>
    </div>

    <template #footer>
      <ModalFooterConfirmButtons
        cancel-text="취소"
        confirm-text="복귀서 제출"
        :disabled="isSubmitDisabled"
        :is-loading="isSubmitting"
        @cancel="handleClose"
        @confirm="handleConfirm"
      />
    </template>
  </BaseModal>
</template>
